import React from 'react';
import { Box, Card, Stack, Typography, LinearProgress, IconButton, alpha } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { TaskListItem } from '../../services/taskService';
import { tokens } from '../../theme';
import { StatusPill } from '../../components/ui';
import {
  AssigneeAvatars, CategoryTag, DueIndicator, MetaItem, PriorityChip, formatDuration,
} from './taskShared';

interface Props {
  task: TaskListItem;
  onOpen: (id: string) => void;
  onMenu?: (event: React.MouseEvent<HTMLElement>, task: TaskListItem) => void;
  /** Drag & drop is wired only when the user may move the card. */
  draggable?: boolean;
  onDragStart?: (event: React.DragEvent, task: TaskListItem) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
}

export const TaskCard: React.FC<Props> = ({
  task, onOpen, onMenu, draggable = false, onDragStart, onDragEnd, isDragging = false,
}) => {
  const overdue = task.isOverdue;
  const hasMeta = task.trackedSeconds > 0 || task.commentCount > 0 || task.attachmentCount > 0;

  return (
    <Card
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, task)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(task.id)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(task.id);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Zadanie: ${task.title}`}
      sx={{
        p: 1.75,
        flexShrink: 0,
        cursor: draggable ? 'grab' : 'pointer',
        borderColor: overdue ? alpha(tokens.danger, 0.45) : tokens.line,
        boxShadow: overdue ? `inset 3px 0 0 ${tokens.danger}` : 'none',
        opacity: isDragging ? 0.45 : 1,
        transition: 'box-shadow .15s ease, border-color .15s ease, opacity .15s ease',
        '&:hover': {
          borderColor: overdue ? tokens.danger : tokens.lineStrong,
          boxShadow: `${overdue ? `inset 3px 0 0 ${tokens.danger}, ` : ''}0 4px 14px ${alpha(tokens.ink, 0.07)}`,
        },
        '&:active': { cursor: draggable ? 'grabbing' : 'pointer' },
        '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={0.5}>
        <Typography
          component="h3"
          sx={{
            flexGrow: 1, minWidth: 0, fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4, color: tokens.ink,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word',
          }}
        >
          {task.title}
        </Typography>
        {onMenu && (
          <IconButton
            size="small"
            aria-label="Akcje zadania"
            onClick={(e) => { e.stopPropagation(); onMenu(e, task); }}
            sx={{ mt: -0.5, mr: -0.75 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
        <PriorityChip priority={task.priority} />
        {task.category && <CategoryTag name={task.category.name} color={task.category.color} />}
        {task.requiresApproval && <StatusPill label="Wymaga akceptacji" tone="neutral" size="small" noDot />}
      </Stack>

      {task.checklist.total > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>Checklista</Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: tokens.inkSoft, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {task.checklist.done}/{task.checklist.total}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={task.checklist.percent ?? 0}
            aria-label={`Postęp checklisty: ${task.checklist.done} z ${task.checklist.total}`}
            sx={{
              height: 5,
              '& .MuiLinearProgress-bar': { bgcolor: task.checklist.percent === 100 ? tokens.success : tokens.plum },
            }}
          />
        </Box>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${tokens.line}` }}
      >
        <Stack direction="row" columnGap={1.25} rowGap={0.5} flexWrap="wrap" alignItems="center" sx={{ minWidth: 0 }}>
          <DueIndicator dueAt={task.dueAt} hasTime={task.dueHasTime} overdue={overdue} showEmpty={!hasMeta} />
          {task.trackedSeconds > 0 && (
            <MetaItem
              icon={<TimerOutlinedIcon />}
              label={formatDuration(task.trackedSeconds)}
              title="Zarejestrowany czas pracy nad zadaniem"
            />
          )}
          {task.commentCount > 0 && (
            <MetaItem icon={<ChatBubbleOutlineIcon />} label={String(task.commentCount)} title="Komentarze" />
          )}
          {task.attachmentCount > 0 && (
            <MetaItem icon={<AttachFileIcon />} label={String(task.attachmentCount)} title="Załączniki" />
          )}
        </Stack>
        <AssigneeAvatars users={task.assignees} size={26} />
      </Stack>
    </Card>
  );
};

export default TaskCard;
